import $ from "jquery";
import debounce from "utils/debounce";
import FilterValue from "./filter-value";
import i18n from "utils/i18n";
import { MultiSelect } from "react-selectize";
import PropTypes from "prop-types";
import React from "react";

/**
 * Select box component for selecting multiple users with auto-complete.
 * This includes the ability to select users by selecting groups.
 *
 * @example
 *   <FilterSelect
 *     className="css-class"
 *     defaultValue="active-user:11|group:5"
 *     id="input-id"
 *     name="input-name"
 *     onChange={onChangeFunction}
 *     onCountsChange={onCountsChangeFunction}
 *   />
 */
class FilterSelect extends React.Component {
  /**
   * Creates the component with the initial state based on props.
   *
   * @param {object} props Initial props
   */
  constructor(props) {
    super(props);

    // Set initial state
    this.state = { isSearching: false, search: "" };
    this.types = this.props.types || ["active-user", "archived-user", "group"];

    // Bind functions to make `this` available
    this.onRemoveValue = this.onRemoveValue.bind(this);
    this.onSearchChange = this.onSearchChange.bind(this);
    this.onValuesChange = this.onValuesChange.bind(this);
    this.performCountLookup = this.performCountLookup.bind(this);
    this.renderNoResultsFound = this.renderNoResultsFound.bind(this);
    this.renderValue = this.renderValue.bind(this);
  }

  /**
   * Populates the selected filters and sets up the component after being mounted.
   *
   * @returns {Promise} The $.ajax Promise is returned if the request is made
   */
  componentDidMount() {
    // Prevent the ESC key when the search is empty so the selected values aren't cleared
    $(this.refs.select).on("keydown", event => {
      // Allow the ESC key if the MultiSelect component's dropdown is open
      if (event.which === 27 && !this.refs.multiSelect.isOpen()) {
        event.stopPropagation();
      }
    });

    return this.populateSelectedValuesFromProps();
  }

  /**
   * Returns whether the selected values include any groups.
   *
   * @returns {boolean} Whether any groups are selected
   */
  hasGroups() {
    return this.value().includes("group");
  }

  /**
   * Removes a selected value from the selected values.
   *
   * @param {object} value The value to remove
   */
  onRemoveValue(value) {
    const { values } = this.state;
    const valueIndex = values.indexOf(value);
    values.splice(valueIndex, 1);
    this.onValuesChange(values);
  }

  /**
   * The callback function passed to the MultiSelect component for handling search
   * changes and fetching new options that match the given search string.
   *
   * @param {string} search The search string for filtering users/groups
   * @returns {Promise} The $.ajax Promise is returned if the request is made
   */
  onSearchChange(search) {
    // Abort an existing request if there is one.
    if (this.searchRequest != null) {
      this.searchRequest.abort();
    }

    // Only perform the search if there are at least 2 characters.
    if (search.length < 2) {
      this.setState({ isSearching: false, search });

      return;
    }

    // Set the state as searching even though we may debounce the AJAX call
    this.setState({ isSearching: true, search });

    // Debounce the AJAX call
    if (debounce(this.onSearchChange, arguments, 200)) {
      return;
    }

    this.searchRequest = $.ajax({
      data: {
        search,
        types: this.types
      },
      type: "POST",
      url: i18n.path("reports_filters")
    })
      .done(data => {
        this.setState({ isSearching: false, options: data.filters });
      })
      .fail(() => {
        this.setState({ isSearching: false });
      })
      .always(() => {
        this.searchRequest = null;
      });

    return this.searchRequest;
  }

  /**
   * The callback function passed to the MultiSelect component for handling changes to the selected values.
   *
   * @param {Array} values The values returned by the MultiSelect component
   */
  onValuesChange(values) {
    if (this.props.multiple === false) {
      values = values.slice(-1);
    }

    this.setState({ values }, () => {
      // Fire the onChange callback when the selected values change
      if (this.props.onChange != null) {
        this.props.onChange(values);
      }

      // If the onCountsChange callback prop is given, perform the count lookup
      if (this.props.onCountsChange != null) {
        this.performCountLookup();
      }
    });
  }

  /**
   * Fetches the total number of users selected based on the selected users/groups.
   *
   * @returns {Promise} The $.ajax Promise is returned if the request is made
   */
  performCountLookup() {
    // Abort an existing request if there is one.
    if (this.countRequest != null) {
      this.countRequest.abort();
    }

    // We don't need to look up the user count if we don't have any groups
    if (!this.hasGroups()) {
      let userCount = this.state.values.length;
      if (userCount === 0) {
        userCount = null;
      }

      this.props.onCountsChange({ userCount });

      return;
    }

    // Signal a user count change in progress
    this.props.onCountsChange({ userCount: undefined });

    // Debounce the AJAX call
    if (debounce(this.performCountLookup, arguments, 200)) {
      return;
    }

    this.countRequest = $.ajax({
      type: "GET",
      url: i18n.path("counts_reports_filter", { id: this.value() })
    })
      .done(data => {
        this.props.onCountsChange({ userCount: data.userCount });
      })
      .always(() => {
        this.countRequest = null;
      });

    return this.countRequest;
  }

  /**
   * Populates the selected values directly from an array of objects.
   *
   * @param {Array} values Ex: [{ groupId: "active-user", id: 5, label: "Billy Bob" }]
   */
  populateSelectedValues(values) {
    this.setState({ options: values, values }, () => {
      if (values.length === 0) {
        return;
      }

      if (this.props.onChange != null) {
        this.props.onChange(values);
      }

      if (this.props.onCountsChange != null) {
        this.performCountLookup();
      }
    });
  }

  /**
   * Populates the selected values from either the readOnlyValue prop or the defaultValue prop.
   *
   * @returns {Promise} The $.ajax Promise is returned if the request is made
   */
  populateSelectedValuesFromProps() {
    // If the readOnlyValue prop is given, don't perform the lookup to populate the selected values
    if (this.props.readOnlyValue != null) {
      this.populateSelectedValues([this.props.readOnlyValue]);

      return;
    }

    // If the defaultValue prop is an object, don't perform the lookup to populate the selected values
    const { defaultValue } = this.props;
    if (typeof defaultValue === "object") {
      this.populateSelectedValues([this.props.defaultValue]);

      return;
    }

    // If no defaultValue is given, don't perform the lookup to populate the selected values
    if (defaultValue == null || defaultValue.trim().length === 0) {
      this.populateSelectedValues([]);

      return;
    }

    // Get the user and group information from the defaultValue string
    return $.ajax({
      data: { types: this.types },
      type: "GET",
      url: i18n.path("reports_filter", { id: this.props.defaultValue })
    })
      .done(data => {
        const values = data.filters;
        const { userCount } = data;

        this.setState({ options: values, values }, () => {
          if (this.props.onChange != null) {
            this.props.onChange(values);
          }

          if (this.props.onCountsChange != null) {
            this.props.onCountsChange({ userCount });
          }
        });
      })
      .fail(() => {
        this.populateSelectedValues([]);
      });
  }

  /**
   * Returns the selected values formatted for form submission.
   *
   * @returns {string} Ex: "active-user:10|archived-user:2|group:15"
   */
  value() {
    if (this.state.values == null) {
      return "";
    }

    const values = [];
    let value;

    for (let i = 0; i < this.state.values.length; i++) {
      value = this.state.values[i];
      values.push(`${value.groupId}:${value.id}`);
    }

    return values.join("|");
  }

  /**
   * Render the select component.
   *
   * @returns {object} The React element
   */
  render() {
    return (
      <div ref="select">
        <MultiSelect
          disabled={this.props.readOnlyValue != null}
          groups={this.renderGroups()}
          hideResetButton={true}
          onSearchChange={this.onSearchChange}
          onValuesChange={this.onValuesChange}
          options={this.state.options}
          placeholder={this.renderPlaceholder()}
          ref="multiSelect"
          renderNoResultsFound={this.renderNoResultsFound}
          renderValue={this.renderValue}
          values={this.state.values}
        />
      </div>
    );
  }

  /**
   * The groups used by the MultiSelect to group users and groups separately in the list.
   *
   * @returns {Array} The groups for the MultiSelect component
   */
  renderGroups() {
    return [
      {
        groupId: "active-user",
        title: i18n.t("activerecord.models.user.other")
      },
      {
        groupId: "archived-user",
        title: i18n.t("manager.archives.index.title")
      },
      {
        groupId: "group",
        title: i18n.t("activerecord.models.group.other")
      }
    ];
  }

  /**
   * Returns the string to render when there are no results found from the current search.
   *   - A "Searching" message is displayed if a search request is in progress.
   *   - A "No results" message is displayed if the search has completed and no results were returned
   *   - A "Type to search" message is displayed if not enough characters are typed to search
   *
   * @returns {object} The React element
   */
  renderNoResultsFound() {
    // "Searching..." if waiting for the AJAX request
    if (this.state.isSearching) {
      return <div className="searching">{i18n.t("report.searching")}</div>;
    }

    // "No results found." if done searching with no matches
    if (this.state.search.length >= 2) {
      return <div className="no-results-found">{i18n.t("report.no_results")}</div>;
    }

    // "Start typing a user's name or a group's name..." when searching users and groups is allowed
    if ((this.types.includes("active-user") || this.types.includes("archived-user")) && this.types.includes("group")) {
      return <div className="no-search">{i18n.t("report.type_to_search_user_group")}</div>;
    }

    // "Start typing a user's name..." when searching only users is allowed
    return <div className="no-search">{i18n.t("report.type_to_search_user")}</div>;
  }

  /**
   * Returns a "Filter users/groups" placeholder message when no text is entered in the input
   * or returns a "Loading" message while the component is restoring its previous state.
   *
   * @returns {string} The placeholder text
   */
  renderPlaceholder() {
    if (this.state.values == null) {
      return i18n.t("report.loading");
    }

    if ((this.types.includes("active-user") || this.types.includes("archived-user")) && this.types.includes("group")) {
      return i18n.t("report.search_placeholder_user_group");
    }

    return i18n.t("report.search_placeholder_user");
  }

  /**
   * Used by the MultiSelect component to render the HTML to represent a selected value.
   *
   * @param {object} value The value to render
   * @returns {object} The React element
   */
  renderValue(value) {
    if (this.props.readOnlyValue != null) {
      return <div className="filter-value read-only">{value.label}</div>;
    }

    return <FilterValue onRemove={this.onRemoveValue} value={value} />;
  }
}

FilterSelect.propTypes = {
  className: PropTypes.string,
  defaultValue: props => {
    if (typeof props.defaultValue === "object") {
      return PropTypes.checkPropTypes(
        {
          defaultValue: PropTypes.shape({
            groupId: PropTypes.string.isRequired,
            id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
            label: PropTypes.string.isRequired
          })
        },
        props,
        "defaultValue",
        "FilterSelect"
      );
    }

    return PropTypes.checkPropTypes({ defaultValue: PropTypes.string }, props, "defaultValue", "FilterSelect");
  },
  id: PropTypes.string,
  multiple: PropTypes.bool,
  name: PropTypes.string,
  onChange: PropTypes.func,
  onCountsChange: PropTypes.func,
  readOnlyValue: PropTypes.shape({
    groupId: PropTypes.string.isRequired,
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    label: PropTypes.string.isRequired
  }),
  types: PropTypes.arrayOf(PropTypes.string)
};

export default FilterSelect;
